import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    Typography,
    Button,
    Box,
    IconButton,
} from '@mui/material';
import {
    Close as CloseIcon,
    School as SchoolIcon,
    MenuBook as MenuBookIcon,
    Settings as SettingsIcon,
    EmojiEvents as TrophyIcon,
    History as HistoryIcon,
    Casino as CasinoIcon,
    AutoAwesome as AutoAwesomeIcon,
    Style as StyleIcon,
    SwapHoriz as SwapHorizIcon,
    RecordVoiceOver as RecordVoiceOverIcon,
    Lock as LockIcon,
    Person as PersonIcon,
    SportsEsports as SportsEsportsIcon,
    Stars as StarsIcon,
    Shuffle as ShuffleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Tutorial screenshots
import img01 from '../assets/images/instructions/01.jpg';
import img02 from '../assets/images/instructions/02.jpg';
import img03 from '../assets/images/instructions/03.jpg';
import img04 from '../assets/images/instructions/04.jpg';
import img05 from '../assets/images/instructions/05.jpg';
import img06 from '../assets/images/instructions/06.jpg';
import img07 from '../assets/images/instructions/07.jpg';
import img08 from '../assets/images/instructions/08.jpg';
import img09 from '../assets/images/instructions/09.jpg';
import img10 from '../assets/images/instructions/10.jpg';
import img11 from '../assets/images/instructions/11.jpg';
import img12 from '../assets/images/instructions/12.jpg';
import img13 from '../assets/images/instructions/13.jpg';

// Map imageKey to actual images
const tutorialImages = {
    instructions: img01,
    settings: img02,
    setup_names: img03,
    setup_mode: img04,
    setup_points: img05,
    setup_dealer: img06,
    ronda_call: img07,
    ronda_auto: img08,
    baciga_torito: img09,
    baciga_discard: img10,
    baciga_call: img11,
    baciga_amarrar: img12,
    scorelog: img13,
};

const SectionDivider = ({ title, color }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 1.5,
            mt: 1,
        }}
    >
        <Box sx={{ flex: 1, height: '2px', bgcolor: color, opacity: 0.3 }} />
        <Typography
            variant="subtitle2"
            sx={{
                color: color,
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: 1,
            }}
        >
            {title}
        </Typography>
        <Box sx={{ flex: 1, height: '2px', bgcolor: color, opacity: 0.3 }} />
    </Box>
);

const TutorialSection = ({ icon, title, description, imageSrc, imageAlt, reverse }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: reverse ? 'row-reverse' : 'row',
            gap: 3,
            alignItems: 'center',
            py: 2,
            px: 1,
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            '&:last-of-type': {
                borderBottom: 'none'
            },
            '@media (orientation: portrait)': { flexDirection: 'column', gap: 1.5 }
        }}
    >
        {/* Image */}
        <Box
            sx={{
                flex: '0 0 45%',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '@media (orientation: portrait)': { flex: 'none', width: '100%' }
            }}
        >
            <img
                src={imageSrc}
                alt={imageAlt}
                style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                }}
            />
        </Box>

        {/* Text content */}
        <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: 'primary.light',
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {icon}
                </Box>
                <Typography variant="subtitle1" fontWeight="bold">
                    {title}
                </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {description}
            </Typography>
        </Box>
    </Box>
);

const TutorialModal = ({ open, onClose, onCloseAndDontShowAgain }) => {
    const { t } = useTranslation();
    const [dontShowAgain, setDontShowAgain] = React.useState(false);

    const handleClose = () => {
        if (dontShowAgain) {
            onCloseAndDontShowAgain();
        } else {
            onClose();
        }
    };

    // General sections
    const generalSections = [
        {
            icon: <MenuBookIcon sx={{ fontSize: 20 }} />,
            titleKey: 'tutorial.section_instructions_title',
            descriptionKey: 'tutorial.section_instructions_desc',
            imageKey: 'instructions',
        },
        {
            icon: <SettingsIcon sx={{ fontSize: 20 }} />,
            titleKey: 'tutorial.section_settings_title',
            descriptionKey: 'tutorial.section_settings_desc',
            imageKey: 'settings',
        },
    ];

    // Game Setup sections
    const gameSetupSections = [
        {
            icon: <PersonIcon sx={{ fontSize: 20 }} />,
            titleKey: 'tutorial.section_setup_names_title',
            descriptionKey: 'tutorial.section_setup_names_desc',
            imageKey: 'setup_names',
        },
        {
            icon: <SportsEsportsIcon sx={{ fontSize: 20 }} />,
            titleKey: 'tutorial.section_setup_mode_title',
            descriptionKey: 'tutorial.section_setup_mode_desc',
            imageKey: 'setup_mode',
        },
        {
            icon: <StarsIcon sx={{ fontSize: 20 }} />,
            titleKey: 'tutorial.section_setup_points_title',
            descriptionKey: 'tutorial.section_setup_points_desc',
            imageKey: 'setup_points',
        },
        {
            icon: <ShuffleIcon sx={{ fontSize: 20 }} />,
            titleKey: 'tutorial.section_setup_dealer_title',
            descriptionKey: 'tutorial.section_setup_dealer_desc',
            imageKey: 'setup_dealer',
        },
    ];

    // Ronda sections
    const rondaSections = [
        {
            icon: <RecordVoiceOverIcon sx={{ fontSize: 20 }} />,
            titleKey: 'tutorial.section_ronda_call_title',
            descriptionKey: 'tutorial.section_ronda_call_desc',
            imageKey: 'ronda_call',
        },
        {
            icon: <AutoAwesomeIcon sx={{ fontSize: 20 }} />,
            titleKey: 'tutorial.section_ronda_auto_title',
            descriptionKey: 'tutorial.section_ronda_auto_desc',
            imageKey: 'ronda_auto',
        },
    ];

    // Baciga sections
    const bacigaSections = [
        {
            icon: <CasinoIcon sx={{ fontSize: 20 }} />,
            titleKey: 'tutorial.section_baciga_torito_title',
            descriptionKey: 'tutorial.section_baciga_torito_desc',
            imageKey: 'baciga_torito',
        },
        {
            icon: <SwapHorizIcon sx={{ fontSize: 20 }} />,
            titleKey: 'tutorial.section_baciga_discard_title',
            descriptionKey: 'tutorial.section_baciga_discard_desc',
            imageKey: 'baciga_discard',
        },
        {
            icon: <StyleIcon sx={{ fontSize: 20 }} />,
            titleKey: 'tutorial.section_baciga_call_title',
            descriptionKey: 'tutorial.section_baciga_call_desc',
            imageKey: 'baciga_call',
        },
        {
            icon: <LockIcon sx={{ fontSize: 20 }} />,
            titleKey: 'tutorial.section_baciga_amarrar_title',
            descriptionKey: 'tutorial.section_baciga_amarrar_desc',
            imageKey: 'baciga_amarrar',
        },
    ];

    // Final section
    const finalSections = [
        {
            icon: <HistoryIcon sx={{ fontSize: 20 }} />,
            titleKey: 'tutorial.section_scorelog_title',
            descriptionKey: 'tutorial.section_scorelog_desc',
            imageKey: 'scorelog',
        },
    ];

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                    maxHeight: '90vh',
                    width: '95vw',
                    maxWidth: '900px',
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                component="div"
                sx={{
                    textAlign: 'center',
                    pt: 2,
                    pb: 1.5,
                    position: 'relative',
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                }}
            >
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                        }}
                    >
                        <SchoolIcon sx={{ fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" component="h2" fontWeight="bold">
                            {t('tutorial.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('tutorial.subtitle')}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            {/* Scrollable content */}
            <DialogContent sx={{ p: 2, overflow: 'auto' }}>
                {/* Welcome message */}
                <Box
                    sx={{
                        bgcolor: 'primary.lighter',
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                    }}
                >
                    <Typography variant="body1" color="text.primary">
                        {t('tutorial.welcome_message')}
                    </Typography>
                </Box>

                {/* General sections */}
                {generalSections.map((section, index) => (
                    <TutorialSection
                        key={section.imageKey}
                        icon={section.icon}
                        title={t(section.titleKey)}
                        description={t(section.descriptionKey)}
                        imageSrc={tutorialImages[section.imageKey]}
                        imageAlt={t(section.titleKey)}
                        reverse={index % 2 === 1}
                    />
                ))}

                {/* Game Setup divider and sections */}
                <SectionDivider title={t('tutorial.divider_setup')} color="#ff9800" />
                {gameSetupSections.map((section, index) => (
                    <TutorialSection
                        key={section.imageKey}
                        icon={section.icon}
                        title={t(section.titleKey)}
                        description={t(section.descriptionKey)}
                        imageSrc={tutorialImages[section.imageKey]}
                        imageAlt={t(section.titleKey)}
                        reverse={index % 2 === 1}
                    />
                ))}

                {/* Ronda divider and sections */}
                <SectionDivider title={t('tutorial.divider_ronda')} color="#4caf50" />
                {rondaSections.map((section, index) => (
                    <TutorialSection
                        key={section.imageKey}
                        icon={section.icon}
                        title={t(section.titleKey)}
                        description={t(section.descriptionKey)}
                        imageSrc={tutorialImages[section.imageKey]}
                        imageAlt={t(section.titleKey)}
                        reverse={index % 2 === 1}
                    />
                ))}

                {/* Baciga divider and sections */}
                <SectionDivider title={t('tutorial.divider_baciga')} color="#9c27b0" />
                {bacigaSections.map((section, index) => (
                    <TutorialSection
                        key={section.imageKey}
                        icon={section.icon}
                        title={t(section.titleKey)}
                        description={t(section.descriptionKey)}
                        imageSrc={tutorialImages[section.imageKey]}
                        imageAlt={t(section.titleKey)}
                        reverse={index % 2 === 1}
                    />
                ))}

                {/* Final sections */}
                <SectionDivider title={t('tutorial.divider_general')} color="#2196f3" />
                {finalSections.map((section, index) => (
                    <TutorialSection
                        key={section.imageKey}
                        icon={section.icon}
                        title={t(section.titleKey)}
                        description={t(section.descriptionKey)}
                        imageSrc={tutorialImages[section.imageKey]}
                        imageAlt={t(section.titleKey)}
                        reverse={index % 2 === 1}
                    />
                ))}
            </DialogContent>

            {/* Footer */}
            <DialogActions
                sx={{
                    flexDirection: 'row',
                    gap: 2,
                    p: 2,
                    pt: 1.5,
                    justifyContent: 'flex-end',
                    borderTop: '1px solid rgba(0,0,0,0.08)',
                    '@media (orientation: portrait)': { flexDirection: 'column', gap: 1 }
                }}
            >
                <Button
                    variant="text"
                    size="medium"
                    onClick={onCloseAndDontShowAgain}
                    sx={{
                        borderRadius: 2,
                        color: 'text.secondary',
                        px: 3,
                        '@media (orientation: portrait)': { width: '100%' }
                    }}
                >
                    {t('tutorial.close_and_dont_show')}
                </Button>
                <Button
                    variant="contained"
                    onClick={onClose}
                    sx={{
                        borderRadius: 2,
                        py: 1,
                        px: 3,
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                        '@media (orientation: portrait)': { width: '100%' }
                    }}
                >
                    {t('tutorial.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TutorialModal;
